import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["compact", "isExpanded", "wrapperStyleFn"];
import FormattedMessage from 'I18n/components/FormattedMessage';
import { useContext, useMemo, useRef, useCallback, useEffect, useState, memo } from 'react';
import { List as ImmutableList } from 'immutable';
import { TransitionGroup } from 'react-transition-group';
import { GREAT_WHITE, OLAF } from 'HubStyleTokens/colors';
import styled from 'styled-components';
import { getFromSenderEmail } from 'conversations-message-history/common-message-format/operators/commonMessageGetters';
import { getIsForward, getInReplyToId
// @ts-expect-error module not typed
} from 'conversations-message-history/common-message-format/operators/commonMessageFormatGetters';
import { getCommonMessage } from 'conversations-thread-data/common-reply/public/operators/commonReplyGetters';
import { getClickedExpandMoreButton } from '../../../usage-tracking/events/metadataEditorInteractions';
// TODO: move to composer-ui
import { ChannelCapabilitiesConfigContext } from 'conversations-thread-view/channel-capabilities/context/ChannelCapabilitiesConfigContext';
import { getSenders, getRecipientKey, getRecipientsFields, getSubject } from 'conversations-thread-data/channel-configuration/public/operators';
import { EXIT_SUB_TRANSITION_TIME_MS, TRANSITION_FN } from '../constants/animationConstants';
import { useMetadataEditorHeight } from '../hooks/useMetadataEditorHeight';
import { useHostedAccountAlert } from '../hooks/useHostedAccountAlert';
import { getIsRequiredMetaDataInfoMissing } from '../operators/getIsRequiredMetaDataInfoMissing';
import AnimatedMetadataField from './AnimatedMetadataField';
import { CollapsedMetadataEditor } from './CollapsedMetadataEditor';
import { FieldVisibilityControls } from './FieldVisibilityControls';
import { Recipient } from './Recipient';
import { Sender } from './Sender';
import { Subject } from './Subject';
import { GenericRecipient } from './GenericRecipient';
import { FromAccountSelect } from './FromAccountSelect';
import { useAllRecipients } from '../hooks/useAllRecipients';
import { EditorTrackerContext } from '../../../usage-tracking/context/EditorTrackerContext';

// These dictate the collapsed height of the MetadataEditor, which is needed
// for the absolutePositioning tricks
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const FIELD_VISIBILITY_CONTROLS_HEIGHT = 40;
export const COMPACT_VERTICAL_PADDING = 4;
function ToLabel({
  isForward,
  inReplyToId
}) {
  if (isForward) {
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "composer-ui.metadata-editor.dynamic-metadata-editor.to-label.forward"
    });
  }
  if (inReplyToId) {
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "composer-ui.metadata-editor.dynamic-metadata-editor.to-label.reply"
    });
  }
  return /*#__PURE__*/_jsx(FormattedMessage, {
    message: "composer-ui.metadata-editor.dynamic-metadata-editor.to-label.default"
  });
}
ToLabel.displayName = 'ToLabel';
const Wrapper = styled(props => {
  const rest = _objectWithoutPropertiesLoose(props, _excluded);
  return /*#__PURE__*/_jsx(TransitionGroup, Object.assign({}, rest));
}).withConfig({
  displayName: "DynamicMetadataEditor__Wrapper",
  componentId: "sc-179p3k2-0"
})(["align-items:stretch;background:", ";border-bottom:1px solid ", ";display:flex;flex-direction:column;padding-bottom:", "px;padding-top:", "px;transition:padding ", "ms ", ";width:100%;flex-shrink:0;", ""], OLAF, GREAT_WHITE, ({
  compact
}) => compact ? COMPACT_VERTICAL_PADDING : 10, ({
  compact
}) => compact ? COMPACT_VERTICAL_PADDING : 10, EXIT_SUB_TRANSITION_TIME_MS, TRANSITION_FN, ({
  wrapperStyleFn,
  isExpanded
}) => wrapperStyleFn && wrapperStyleFn({
  isExpanded
}));
const Fields = {
  FROM: 'FROM',
  SUBJECT: 'SUBJECT',
  ALL: 'ALL'
};
const noop = () => {};
const DynamicMetadataEditor = /*#__PURE__*/memo(({
  expanded,
  onExpand,
  reply,
  onChange,
  onHeightChange,
  inboxId,
  isComposer,
  wrapperStyleFn,
  senderSubjectExpanded,
  toggleSenderSubjectExpand,
  useWhatsAppCompose
}) => {
  const {
    capabilitiesBlob
  } = useContext(ChannelCapabilitiesConfigContext);
  const {
    trackEvent
  } = useContext(EditorTrackerContext);
  const recipientsFields = useMemo(() => getRecipientsFields(capabilitiesBlob) || [], [capabilitiesBlob]);
  const sendersConfig = getSenders(capabilitiesBlob);
  const subjectSettings = getSubject(capabilitiesBlob);
  const [recipientFieldsExpansionState, setRecipientFieldsExpansionState] = useState(() => ImmutableList(new Array(recipientsFields.length).fill(false)));
  const [forcedCollapse, setForcedCollapse] = useState(false);
  const [showCCBCCIconButtons, setShowCCBCCIconButtons] = useState(false);
  const isRequiredMetaDataInfoMissing = getIsRequiredMetaDataInfoMissing(reply);
  useEffect(() => {
    if (isRequiredMetaDataInfoMissing) {
      setForcedCollapse(false);
    }
  }, [isRequiredMetaDataInfoMissing]);
  const shouldExpand = !forcedCollapse && expanded;
  // creates a delayed version of the passed expanded prop. This is
  // used to trigger hiding/showing of form fields so that each has
  // an additional render cycle to receive the changed `expanded`
  // value before its props are frozen for the unmounting animation.
  const [delayedExpanded, setDelayedExpanded] = useState(shouldExpand);
  useEffect(() => {
    setDelayedExpanded(shouldExpand);
  }, [shouldExpand]);
  const recipientFieldRefs = useRef(null);
  const handleRowEntered = useCallback(() => {
    if (recipientFieldRefs && recipientFieldRefs.current) {
      const index = recipientFieldRefs.current.index;
      const currentField = recipientFieldRefs.current.currentField;
      const hasValue = recipientFieldRefs.current.hasValue;
      if (!hasValue && index && index !== 0 && currentField) {
        currentField.focus();
      }
    }
  }, []);
  const handleRowExited = useCallback(() => {
    if (recipientFieldRefs && recipientFieldRefs.current) {
      const defaultField = recipientFieldRefs.current.defaultField;
      if (defaultField) defaultField.focus();
    }
  }, []);

  // disable enter + appear animations on mount to avoid the initial
  // fields "sliding" up after the loading state finishes
  const [enableAnimations, setEnableAnimations] = useState(false);
  useEffect(() => {
    setEnableAnimations(true);
  }, []);

  // Get initial state from reply
  const commonMessage = getCommonMessage(reply);
  const inReplyToId = getInReplyToId(commonMessage);
  const isForward = getIsForward(commonMessage);
  const {
    allRecipients
  } = useAllRecipients({
    commonMessage,
    useWhatsAppCompose
  });
  const fromAddress = getFromSenderEmail(commonMessage);
  useHostedAccountAlert({
    inboxId,
    fromAddress,
    isComposer
  });
  useEffect(() => {
    if (!shouldExpand) {
      setRecipientFieldsExpansionState(ImmutableList(new Array(recipientsFields.length).fill(false)));
      setShowCCBCCIconButtons(false);
    }
  }, [shouldExpand, recipientsFields]);
  const isShowSenderSubjectFields = senderSubjectExpanded;
  const handleSetExpanded = useCallback(() => {
    const newExpansionState = [];
    for (const recipient of recipientsFields) {
      const recipientKey = getRecipientKey(recipient);
      const recipientValue = recipientKey && allRecipients.get(recipientKey);
      newExpansionState.push(Boolean(recipientValue && recipientValue.size > 0));
    }
    setRecipientFieldsExpansionState(ImmutableList(newExpansionState));
    setForcedCollapse(false);
    setShowCCBCCIconButtons(true);
    onExpand();
  }, [onExpand, recipientsFields, allRecipients]);
  const handleMoreButtonOnClick = useCallback(() => {
    trackEvent(getClickedExpandMoreButton());
    toggleSenderSubjectExpand();
  }, [toggleSenderSubjectExpand, trackEvent]);
  const {
    setAnimatedFieldSize
  } = useMetadataEditorHeight({
    onHeightChange
  });
  const senderSubjectFields = /*#__PURE__*/_jsxs(_Fragment, {
    children: [sendersConfig && isShowSenderSubjectFields && inboxId ? /*#__PURE__*/_jsx(AnimatedMetadataField, {
      fieldName: Fields.FROM,
      onEntered: handleRowEntered,
      onHeightChange: setAnimatedFieldSize,
      children: useWhatsAppCompose ? /*#__PURE__*/_jsx(FromAccountSelect, {
        reply: reply,
        onChange: onChange,
        inboxId: inboxId,
        senderField: sendersConfig.fieldName
      }) : /*#__PURE__*/_jsx(Sender, {
        reply: reply,
        onChange: onChange,
        inboxId: inboxId
      })
    }, Fields.FROM) : null, subjectSettings && isShowSenderSubjectFields ? /*#__PURE__*/_jsx(AnimatedMetadataField, {
      fieldName: Fields.SUBJECT,
      onEntered: handleRowEntered,
      onHeightChange: setAnimatedFieldSize,
      children: /*#__PURE__*/_jsx(Subject, {
        reply: reply,
        onChange: onChange,
        handleSetExpanded: noop,
        settings: subjectSettings
      })
    }, Fields.SUBJECT) : null]
  });
  const toLabel = /*#__PURE__*/_jsx(ToLabel, {
    isForward: isForward,
    inReplyToId: inReplyToId
  });
  if (!delayedExpanded) {
    /* COLLAPSED EDITOR VIEW */
    return /*#__PURE__*/_jsxs(Wrapper, {
      "data-test-id": "metadata-editor-wrapper",
      isExpanded: expanded,
      compact: !delayedExpanded,
      enter: enableAnimations,
      wrapperStyleFn: wrapperStyleFn,
      children: [/*#__PURE__*/_jsx(AnimatedMetadataField, {
        fieldName: Fields.ALL,
        classNames: "animated-row-reverse",
        onHeightChange: setAnimatedFieldSize,
        children: /*#__PURE__*/_jsx(CollapsedMetadataEditor, {
          allRecipients: allRecipients,
          handleMoreButtonOnClick: handleMoreButtonOnClick,
          handleSetExpanded: handleSetExpanded,
          reply: reply,
          toLabel: toLabel,
          onChange: onChange,
          showSenderSubjectFields: senderSubjectExpanded,
          useWhatsAppCompose: useWhatsAppCompose
        })
      }, Fields.ALL), senderSubjectFields]
    }, 'collapsed');
  }
  return /*#__PURE__*/_jsxs(Wrapper, {
    "data-test-id": "metadata-editor-wrapper",
    isExpanded: expanded,
    compact: true,
    enter: enableAnimations,
    wrapperStyleFn: wrapperStyleFn,
    children: [recipientsFields.map((recipientField, index) => {
      const recipientKey = getRecipientKey(recipientField);
      return recipientFieldsExpansionState.get(index) || index === 0 ? /*#__PURE__*/_jsxs(AnimatedMetadataField, {
        onEntered: handleRowEntered,
        onExited: handleRowExited
        // Disable animation for TO field when input autofocus is true
        // as this results in ugly slide up of the recipient name selector
        ,
        classNames: index === 0 ? '' : 'animated-row',
        fieldName: recipientKey,
        onHeightChange: setAnimatedFieldSize,
        children: [useWhatsAppCompose ? /*#__PURE__*/_jsx(GenericRecipient, {
          allRecipients: allRecipients,
          delayedExpanded: delayedExpanded,
          recipientFieldsExpansionState: recipientFieldsExpansionState,
          onChange: onChange,
          reply: reply,
          handleSetExpanded: handleSetExpanded,
          index: index,
          isComposer: isComposer,
          recipientField: recipientField,
          toLabel: toLabel,
          ref: recipientFieldRefs
        }) : /*#__PURE__*/_jsx(Recipient, {
          allRecipients: allRecipients,
          delayedExpanded: delayedExpanded,
          recipientFieldsExpansionState: recipientFieldsExpansionState,
          onChange: onChange,
          reply: reply,
          handleSetExpanded: handleSetExpanded,
          index: index,
          isComposer: isComposer,
          recipientField: recipientField,
          toLabel: toLabel,
          ref: recipientFieldRefs
        }), index === 0 ? /*#__PURE__*/_jsx(FieldVisibilityControls, {
          handleCollapseButtonOnClick: toggleSenderSubjectExpand,
          recipientFieldsExpansionState: recipientFieldsExpansionState,
          recipientsFields: recipientsFields,
          setRecipientFieldsExpansionState: setRecipientFieldsExpansionState,
          showCCBCCIconButtons: showCCBCCIconButtons,
          showSenderSubjectFields: senderSubjectExpanded
        }) : null]
      }, recipientKey) : null;
    }), senderSubjectFields]
  }, 'expanded');
});
DynamicMetadataEditor.displayName = 'DynamicMetadataEditor';
export default DynamicMetadataEditor;